/**
 * What the SBOM application holds for this revision, on the quality record, without becoming it.
 *
 * WHAT THIS PANE IS FOR. The chip in the header answers "is there one". This answers the four
 * questions somebody preparing a submission asks next, in order: does the document describe THIS
 * revision; how much of it can be looked up; how much of the vulnerability work is outstanding;
 * and has anybody signed. Everything here is a count or a fact about the document itself.
 *
 * WHAT IT DELIBERATELY DOES NOT SHOW. No component table, no findings list, no assessment rows, no
 * actions. Those ARE the composition page, and a summary that grows into a second composition page
 * is how two screens start disagreeing about the same record. Every one of those is one click
 * away, and the link is the point of the pane rather than an afterthought on it.
 *
 * IT ASKS ONE QUESTION OF ONE SERVICE. The SBOM application answers with a summary it owns, so
 * this file contains no arithmetic and no judgement about what the numbers MEAN -- if it did, the
 * meaning would live in two applications and drift.
 *
 * A BLANK IS NOT A ZERO, which is why every count is rendered through `Count` rather than with
 * `?? 0`. "0 unassessed" says the work is done; "we could not tell you" says nothing of the kind,
 * and on a quality record the difference is the whole point.
 */
import React, {useContext, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Divider, Typography} from "@mui/material";
import {OpenInNewOutlined} from "@mui/icons-material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {buildSsoHandoffUrl, handoffVisibility} from "../../util/ssoAuthenticate";
import {Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";

const SBOM_URL = process.env.REACT_APP_SBOM_URL || "https://sbom.cres-intelligence.io";

type Summary = {
    Present?: boolean;
    Documents?: number | null;
    Document?: {
        Format?: string | null; SpecVersion?: string | null; FileName?: string | null;
        Source?: string | null; IngestedDatetime?: string | null;
        Confirmed?: boolean; ConfirmedDatetime?: string | null; Inherited?: boolean;
        Evidence?: {Class?: string; Rank?: number; Label?: string; Means?: string} | null;
    };
    Components?: {Total?: number | null; Searchable?: number | null;
                  Unverifiable?: number | null; Unmatchable?: number | null};
    Vulnerabilities?: {Total?: number | null; Unassessed?: number | null;
                       Assessable?: boolean | null; NotAssessableReason?: string | null};
    Signatures?: {Signed?: number | null; Unsigned?: number | null};
};

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 16);
    return at.toLocaleString(undefined,
        {day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"});
}

/** A number, or an em dash. NEVER a zero substituted for an absent answer. */
function Count({value, suffix}: {value?: number | null; suffix?: string}) {
    if (value === null || value === undefined) {
        return <Box component="span" sx={{color: Grey5}} title="Not reported">—</Box>;
    }
    return <>{value}{suffix ? ` ${suffix}` : ""}</>;
}

function Row({label, children}: {label: string; children: React.ReactNode}) {
    return (
        <Box sx={{display: "flex", gap: "12px", padding: "7px 0", alignItems: "baseline"}}>
            <Typography sx={{fontSize: 12.5, color: Grey7, width: 190, flexShrink: 0}}>
                {label}
            </Typography>
            <Typography component="div" sx={{fontSize: 13, color: Grey17}}>{children}</Typography>
        </Box>
    );
}

export default function SbomPane({revisionUSID}: {revisionUSID?: string | null}) {
    const appContext: any = useContext(AppContext);
    const sessionContext = appContext?.sessionContext;
    const [summary, setSummary] = useState<Summary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        setLoading(true);

        makeCresAxios(sessionContext)
            .get("t1/v1/sbom_presence", {params: {RevisionUSID: revisionUSID, Detail: true}})
            .then((response: any) => { if (!cancelled) setSummary(response?.data ?? null); })
            .catch(() => { if (!cancelled) setSummary(null); })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [revisionUSID, sessionContext]);

    const open = () => {
        window.open(
            buildSsoHandoffUrl(`${SBOM_URL}/compositions/${revisionUSID}`,
                               sessionContext?.getAccessToken?.() ?? null,
                               handoffVisibility(sessionContext)),
            "_blank", "noopener");
    };

    if (loading) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Asking the SBOM application…</Typography>
        </Box>;
    }

    // THE TAB'S EXISTENCE ALREADY SAID THERE IS ONE, so this cannot say there is not. The tab is
    // offered on the strength of the light presence read and this pane makes a second, heavier
    // one; when the two disagree the honest reading is that the detail could not be fetched --
    // and saying "no bill of materials is recorded" would contradict the tab the reader just
    // clicked. Seen for real: with the summary action deployed but its provider map not yet
    // refreshed, the tab appeared and the pane denied the composition existed.
    if (!summary?.Present) {
        return <Typography sx={{fontSize: 13, color: Grey7, padding: "20px 0", maxWidth: 560,
                                lineHeight: 1.6}}>
            The SBOM application holds a bill of materials for this revision, but its details
            could not be read just now. Open it in the SBOM application, or try again shortly.
            <Box sx={{marginTop: "12px"}}>
                <Button size="small" variant="outlined" onClick={open}
                        endIcon={<OpenInNewOutlined sx={{fontSize: 15}}/>}>
                    Open in SBOM
                </Button>
            </Box>
        </Typography>;
    }

    const document = summary.Document ?? {};
    const components = summary.Components ?? {};
    const vulnerabilities = summary.Vulnerabilities ?? {};
    const signatures = summary.Signatures ?? {};

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 760}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "4px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    The document
                </Typography>
                <Box sx={{flex: 1}}/>
                <Button size="small" variant="outlined" onClick={open}
                        endIcon={<OpenInNewOutlined sx={{fontSize: 15}}/>}>
                    Open in SBOM
                </Button>
            </Box>

            <Row label="Format">
                {document.Format ?? "—"}{document.SpecVersion ? ` ${document.SpecVersion}` : ""}
            </Row>
            <Row label="File">{document.FileName ?? "—"}</Row>
            <Row label="Source">{document.Source ?? "—"}</Row>
            {/* HOW STRONG THE READING IS, on the quality record rather than only in the SBOM
                application — this is the page somebody stands on when they decide whether a
                document is good enough to put in a submission. A list read out of a lockfile is a
                record of what a resolver WOULD install; a document a supplier signed is a claim
                about what they shipped. The rank, not this page, decides which of them gets the
                warning colour. */}
            {document.Evidence?.Label ? (
                <Row label="Evidence">
                    <Box component="span"
                         sx={{color: (document.Evidence.Rank ?? 3) > 3 ? "#B54708" : "inherit"}}>
                        {document.Evidence.Label}
                    </Box>
                    {document.Evidence.Means ? (
                        <Typography sx={{fontSize: 12, color: Grey7, marginTop: "2px"}}>
                            {document.Evidence.Means}
                        </Typography>
                    ) : null}
                </Row>
            ) : null}
            <Row label="Ingested">{when(document.IngestedDatetime)}</Row>
            {/* CONFIRMED IS THE CLAIM A SUBMISSION RESTS ON. An ingested document says somebody
                uploaded a file; a confirmed one says somebody checked it describes THIS revision,
                and only the second belongs in a filing. */}
            <Row label="Confirmed against this revision">
                {document.Confirmed
                    ? <b>Yes — {when(document.ConfirmedDatetime)}</b>
                    : <Box component="span" sx={{color: "#B54708"}}>
                        Not yet — this document has been ingested but nobody has confirmed it
                        describes this revision
                      </Box>}
            </Row>
            {document.Inherited ? (
                <Row label="Inherited">Yes — carried from another subject rather than uploaded here</Row>
            ) : null}
            {(summary.Documents ?? 1) > 1 ? (
                <Row label="Documents held">
                    <Count value={summary.Documents}/> — the current one is shown; the rest are
                    kept and are visible in the SBOM application
                </Row>
            ) : null}

            <Divider sx={{margin: "14px 0", borderColor: Grey2}}/>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "4px"}}>
                What is in it
            </Typography>
            <Row label="Components"><Count value={components.Total}/></Row>
            {/* "14 components" says nothing about whether any of them can be looked up, and a
                component nobody can match to an advisory is the one that hurts in year eight. */}
            <Row label="Can be looked up">
                <Count value={components.Searchable}/> searchable ·{" "}
                <Count value={components.Unverifiable}/> unverifiable ·{" "}
                <Count value={components.Unmatchable}/> unmatchable
            </Row>

            <Divider sx={{margin: "14px 0", borderColor: Grey2}}/>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "4px"}}>
                Where the work stands
            </Typography>
            <Row label="Vulnerabilities found"><Count value={vulnerabilities.Total}/></Row>
            {/* THE NUMBER A RELEASE DEPENDS ON, and the reason it may be absent rather than zero. */}
            <Row label="Still undecided">
                {vulnerabilities.Assessable === false
                    ? <Box component="span" sx={{color: Grey7}}>
                        {vulnerabilities.NotAssessableReason
                         ?? "This composition cannot be assessed yet."}
                      </Box>
                    : <Count value={vulnerabilities.Unassessed}/>}
            </Row>
            {/* THE PART 11 FACT, and the one a chip cannot carry. */}
            <Row label="Decisions signed">
                <Count value={signatures.Signed}/> signed ·{" "}
                <Count value={signatures.Unsigned}/> unsigned
            </Row>

            <Typography sx={{fontSize: 12, color: Grey5, marginTop: "16px", lineHeight: 1.6}}>
                Components, findings and decisions are recorded in the SBOM application and are not
                repeated here. This is a summary of what it holds for this revision.
            </Typography>
        </Box>
    );
}
